/**
 * A forge's arche settings, like the Arche Builder's "Configure": icon, name,
 * short and long description, price, Arche Store listing, public access and
 * full screen. Its own dialog, apart from publishing: the settings are saved
 * on the forge and laid over the workspace's fias-plugin.json when it
 * publishes (every publish rewrites the arche's Store fields from it).
 *
 * The icon is the exception to "at the next publish" only for an arche that
 * is not live yet: a draft arche gets it at once, a live one when it is next
 * published, so the live arche never changes before review — the dialog says
 * which.
 */
import { useEffect, useRef, useState } from 'react';
import { ImageOff, Loader2, Upload } from 'lucide-react';
import type { ForgePublishOptions } from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { GameListingFields } from '@/components/GameListingFields';
import { FORGE_ICON_TYPES, useForgePublishSettings } from '../hooks/useForgePublishSettings';
import { upcomingArcheIcon } from './archeSettingsSummary';

/** Mirror the server's bounds (FORGE_PUBLISH_*). */
const NAME_MAX = 80;
const DESCRIPTION_MAX = 500;
const ABOUT_MAX = 5000;
const PRICE_MAX_DOLLARS = 1000;

export interface ForgeArcheSettingsDialogProps {
  forgeId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function errorText(err: unknown): string {
  return err instanceof Error ? err.message : 'Something went wrong. Try again.';
}

export function ForgeArcheSettingsDialog({
  forgeId,
  open,
  onOpenChange,
}: ForgeArcheSettingsDialogProps) {
  const {
    view,
    isLoading,
    error: loadError,
    save,
    uploadIcon,
    removeIcon,
  } = useForgePublishSettings(forgeId, open);
  const [draft, setDraft] = useState<ForgePublishOptions | null>(null);
  const [price, setPrice] = useState('');
  const [busy, setBusy] = useState<null | 'save' | 'icon'>(null);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Start from the server's view each time the dialog opens.
  useEffect(() => {
    if (!open) {
      setDraft(null);
      setError(null);
      return;
    }
    if (view && !draft) {
      setDraft(view.settings);
      setPrice(
        view.settings.pricing.model === 'fixed'
          ? (view.settings.pricing.priceCents / 100).toFixed(2)
          : '',
      );
    }
  }, [open, view, draft]);

  const update = (patch: Partial<ForgePublishOptions>) =>
    setDraft((d) => (d ? { ...d, ...patch } : d));

  const paid = draft?.pricing.model === 'fixed';
  const priceCents = Math.round(Number(price) * 100);
  const priceValid =
    !paid ||
    (Number.isFinite(priceCents) && priceCents >= 1 && priceCents <= PRICE_MAX_DOLLARS * 100);
  const game = draft?.game ?? { isGame: false, genres: [] };
  const gameValid = !game.isGame || game.genres.length > 0;
  const valid =
    !!draft &&
    draft.name.trim().length > 0 &&
    draft.description.trim().length > 0 &&
    priceValid &&
    gameValid;

  const options = (): ForgePublishOptions => ({
    ...draft!,
    name: draft!.name.trim(),
    description: draft!.description.trim(),
    about: draft!.about.trim(),
    pricing: paid ? { model: 'fixed', priceCents } : { model: 'free' },
  });

  const onSave = async () => {
    if (!valid) return;
    setBusy('save');
    setError(null);
    try {
      await save(options());
      onOpenChange(false);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(null);
    }
  };

  const onIconFile = async (file: File | undefined) => {
    if (!file) return;
    setBusy('icon');
    setError(null);
    try {
      await uploadIcon(file);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(null);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const onRemoveIcon = async () => {
    setBusy('icon');
    setError(null);
    try {
      await removeIcon();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(null);
    }
  };

  const pending = view?.icon.pending ?? null;
  const shownIcon = view ? upcomingArcheIcon(view) : null;
  const live = view?.archeStatus === 'published';

  return (
    <Dialog open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
      <DialogContent
        className="sm:max-w-[560px] max-h-[90vh] flex flex-col"
        data-testid="forge-arche-settings-dialog"
      >
        <DialogHeader>
          <DialogTitle>Arche settings</DialogTitle>
          <DialogDescription>
            How your arche appears to others. Saved settings are used the next time you publish to
            production.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          {!draft ? (
            <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
              {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
              {loadError ?? 'Loading the arche settings…'}
            </div>
          ) : (
            <div className="space-y-4 py-2">
              <div className="flex items-center gap-3">
                <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg border bg-muted">
                  {shownIcon ? (
                    <img src={shownIcon} alt="Arche icon" className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                      <ImageOff className="h-5 w-5" />
                    </div>
                  )}
                </div>
                <div className="space-y-1">
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fileRef.current?.click()}
                      disabled={busy !== null}
                    >
                      {busy === 'icon' ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                      ) : (
                        <Upload className="h-4 w-4 mr-1.5" />
                      )}
                      {shownIcon ? 'Change icon' : 'Upload icon'}
                    </Button>
                    {shownIcon && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => void onRemoveIcon()}
                        disabled={busy !== null}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {pending && live
                      ? 'The new icon appears when you next publish.'
                      : 'PNG, JPEG, WebP or SVG, up to 5 MB.'}
                  </p>
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept={FORGE_ICON_TYPES.join(',')}
                  className="hidden"
                  aria-label="Icon file"
                  onChange={(e) => void onIconFile(e.target.files?.[0])}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="forge-settings-name">Name</Label>
                <Input
                  id="forge-settings-name"
                  value={draft.name}
                  maxLength={NAME_MAX}
                  onChange={(e) => update({ name: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="forge-settings-description">Short description</Label>
                <Input
                  id="forge-settings-description"
                  value={draft.description}
                  maxLength={DESCRIPTION_MAX}
                  onChange={(e) => update({ description: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="forge-settings-about">About (optional)</Label>
                <Textarea
                  id="forge-settings-about"
                  value={draft.about}
                  maxLength={ABOUT_MAX}
                  rows={4}
                  onChange={(e) => update({ about: e.target.value })}
                  placeholder="A longer description for the arche's page"
                />
              </div>

              <div className="space-y-1.5">
                <Label>Price</Label>
                <div className="flex items-center gap-3 text-sm">
                  <label className="flex items-center gap-1.5">
                    <input
                      type="radio"
                      name="forge-settings-pricing"
                      checked={!paid}
                      onChange={() => update({ pricing: { model: 'free' } })}
                    />
                    Free
                  </label>
                  <label className="flex items-center gap-1.5">
                    <input
                      type="radio"
                      name="forge-settings-pricing"
                      checked={paid}
                      onChange={() => {
                        update({
                          pricing: { model: 'fixed', priceCents: priceCents || 99 },
                          isPublic: false,
                        });
                        if (!price) setPrice('0.99');
                      }}
                    />
                    Paid, per use
                  </label>
                  {paid && (
                    <span className="flex items-center gap-1">
                      $
                      <Input
                        aria-label="Price per use in dollars"
                        className="h-8 w-24"
                        inputMode="decimal"
                        value={price}
                        onChange={(e) => setPrice(e.target.value)}
                      />
                    </span>
                  )}
                </div>
                {!priceValid && (
                  <p className="text-xs text-destructive">
                    Enter a price from $0.01 to ${PRICE_MAX_DOLLARS}.
                  </p>
                )}
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <Label htmlFor="forge-settings-listed" className="font-normal">
                    Listed in the Arche Store
                  </Label>
                  <Switch
                    id="forge-settings-listed"
                    checked={draft.isListed}
                    onCheckedChange={(v) => update({ isListed: v })}
                  />
                </div>
                <div className="flex items-center justify-between gap-3">
                  <Label htmlFor="forge-settings-public" className="font-normal">
                    Public access (opens without signing in)
                    {paid && (
                      <span className="block text-xs text-muted-foreground">
                        Not available for a paid arche.
                      </span>
                    )}
                  </Label>
                  <Switch
                    id="forge-settings-public"
                    checked={draft.isPublic}
                    disabled={paid}
                    onCheckedChange={(v) => update({ isPublic: v })}
                  />
                </div>
                <div className="flex items-center justify-between gap-3">
                  <Label htmlFor="forge-settings-fullscreen" className="font-normal">
                    Full screen (no Fias header or sidebar)
                  </Label>
                  <Switch
                    id="forge-settings-fullscreen"
                    checked={draft.isFullScreen}
                    onCheckedChange={(v) => update({ isFullScreen: v })}
                  />
                </div>
                <GameListingFields
                  idPrefix="forge-settings"
                  value={game}
                  onChange={(next) => update({ game: next })}
                  maxGenres={view?.gameGenreLimit}
                />
              </div>

              {error && (
                <p role="alert" className="text-sm text-destructive">
                  {error}
                </p>
              )}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy !== null}>
            Cancel
          </Button>
          <Button onClick={() => void onSave()} disabled={!valid || busy !== null}>
            {busy === 'save' && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
